import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { useForm } from "react-hook-form";

import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { claimApi } from "@/main/api";
import { KeyValue, KeyValueDefault } from "@/main/models/common";
import { getTranslateByKey } from "@/main/services/utils";

import * as S from "./styles";

const useStyles = makeStyles(() =>
	createStyles({
		root: {
			"& .MuiPaper-root": {
				padding: "1.5rem !important",
				maxWidth: "500px",
			},
		},
		content: {
			padding: "0",
			overflowX: "hidden",
		},
		actions: {
			padding: "1.5rem 0 0",
		},
	}),
);

interface IPrintSupplierPartsReportDialogProps {
	isOpen: boolean;
	guidClaim: string;
	onCancel: () => void;
	onConfirm: (guidSupplier: string) => void;
}

interface IPrintSupplierPartsReportForm {
	supplier: KeyValue | null;
}

function PrintSupplierPartsReportDialog({
	isOpen,
	guidClaim,
	onCancel,
	onConfirm,
}: Readonly<IPrintSupplierPartsReportDialogProps>) {
	const classes = useStyles();
	const [suppliers, setSuppliers] = useState<KeyValue[]>([]);
	const [isLoadingSuppliers, setIsLoadingSuppliers] = useState<boolean>(false);

	const form = useForm<IPrintSupplierPartsReportForm>({
		mode: "onChange",
		defaultValues: { supplier: null },
	});

	useEffect(() => {
		if (!isOpen) return;

		form.reset({ supplier: null });
		setSuppliers([]);
		setIsLoadingSuppliers(true);
		claimApi
			.getSupplierPartsReportSuppliers(guidClaim)
			.then((response) => setSuppliers(response.data.result ?? []))
			.catch(() => setSuppliers([]))
			.finally(() => setIsLoadingSuppliers(false));
	}, [isOpen, guidClaim]);

	function onSubmit(data: IPrintSupplierPartsReportForm) {
		if (data.supplier) onConfirm(String(data.supplier.key));
	}

	return (
		<Dialog open={isOpen} className={classes.root} onClose={onCancel}>
			<DialogContent className={classes.content}>
				<S.Container>
					<S.Title id="id-print-supplier-parts-report-title">
						{getTranslateByKey("reportTemplateType.supplierPartsReport")}
					</S.Title>
					<S.Subtitle>{getTranslateByKey("information.selectSupplierToPrintPartsReport")}</S.Subtitle>
					<form id="form-print-supplier-parts-report" onSubmit={form.handleSubmit(onSubmit)} noValidate>
						<S.FieldWrapper>
							<KogAutoCompleteControlled
								id="id-print-supplier-parts-report-supplier"
								className="ignore-click"
								required
								label={getTranslateByKey("information.supplier")}
								nameCustom="supplier"
								errorMessageCustom=""
								control={form.control}
								setValueCustom={form.setValue}
								options={suppliers}
								isLoading={isLoadingSuppliers}
								rulesCustom={{ required: true }}
								hasErrorCustom={false}
								onChangeCustom={(selected: KeyValue | KeyValueDefault | null) =>
									form.setValue("supplier", selected, { shouldValidate: true })
								}
							/>
						</S.FieldWrapper>
					</form>
				</S.Container>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<S.AreaFooterButtons>
					<SecondaryButton id="id-btn-print-supplier-parts-report-cancel" onClick={onCancel}>
						{getTranslateByKey("information.cancel")}
					</SecondaryButton>
					<Button id="id-btn-print-supplier-parts-report-confirm" type="submit" form="form-print-supplier-parts-report">
						{getTranslateByKey("information.confirm")}
					</Button>
				</S.AreaFooterButtons>
			</DialogActions>
		</Dialog>
	);
}

export default PrintSupplierPartsReportDialog;
